export interface TabItem {
  id: string;
  label: string;
  badge?: string | undefined;
}

export class AppTabs extends HTMLElement {
  private _tabs: TabItem[] = [];
  private _activeTabId: string = "";

  static get observedAttributes() {
    return ["active"];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    this.render();
  }

  set tabs(items: TabItem[]) {
    this._tabs = items;
    if (!this._activeTabId && items.length > 0 && items[0]) {
      this._activeTabId = items[0].id;
    }
    this.render();
  }

  get active(): string {
    return this._activeTabId;
  }

  set active(id: string) {
    this._activeTabId = id;
    this.setAttribute("active", id);
    this.render();
    this.dispatchEvent(new CustomEvent("tab-change", { detail: { tabId: id } }));
  }

  private render() {
    if (this._tabs.length === 0) {
      const childButtons = this.querySelectorAll("[data-tab]");
      if (childButtons.length > 0) {
        this._tabs = Array.from(childButtons).map(b => ({
          id: b.getAttribute("data-tab") || "",
          label: b.textContent || "",
          badge: b.getAttribute("data-badge") || undefined
        }));
      }
    }

    const tabsHtml = this._tabs.map(tab => {
      const isActive = tab.id === this._activeTabId;
      return `
        <button
          type="button"
          class="btn-tab ${isActive ? "active" : ""}"
          data-tab-id="${tab.id}"
          role="tab"
          aria-selected="${String(isActive)}"
          style="padding: 8px 16px; background: transparent; border: none; border-bottom: 2px solid ${isActive ? "var(--color-primary)" : "transparent"}; color: ${isActive ? "var(--color-primary)" : "var(--color-text-secondary)"}; font-weight: ${isActive ? "600" : "500"}; font-size: 0.8125rem; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all var(--transition-fast);"
        >
          <span>${tab.label}</span>
          ${tab.badge ? `<span style="font-size: 0.6875rem; background: var(--color-surface-secondary); padding: 1px 6px; border-radius: 9999px;">${tab.badge}</span>` : ""}
        </button>
      `;
    }).join("");

    this.innerHTML = `
      <div role="tablist" style="display: flex; gap: 4px; border-bottom: 1px solid var(--color-border-default); margin-bottom: 16px;">
        ${tabsHtml}
      </div>
    `;

    this.querySelectorAll<HTMLButtonElement>(".btn-tab").forEach(btn => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-tab-id") || "";
        this.active = id;
      });
    });
  }
}

if (!customElements.get("app-tabs")) {
  if (!customElements.get("app-tabs")) {
    customElements.define("app-tabs", AppTabs);
  }
}

export class AppAccordion extends HTMLElement {
  static get observedAttributes() {
    return ["title", "open"];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    this.render();
  }

  private render() {
    const title = this.getAttribute("title") || "Seção Técnica";
    const isOpen = this.hasAttribute("open");
    const content = this.innerHTML;

    this.innerHTML = `
      <details ${isOpen ? "open" : ""} style="border: 1px solid var(--color-border-default); border-radius: var(--radius-sm); margin-bottom: 8px; background: var(--color-surface-primary);">
        <summary style="padding: 12px 16px; font-weight: 600; font-size: 0.875rem; color: var(--ia-primary); cursor: pointer; user-select: none; background: var(--color-surface-secondary); outline: none;">
          ${title}
        </summary>
        <div style="padding: 16px; border-top: 1px solid var(--color-border-subtle);">
          ${content}
        </div>
      </details>
    `;
  }
}

if (!customElements.get("app-accordion")) {
  if (!customElements.get("app-accordion")) {
    customElements.define("app-accordion", AppAccordion);
  }
}
